import React, { useMemo } from 'react';
import { NeverChildren, withCoreUIType } from '@tanium/coreui-utils';
import { Box } from '@tanium/react-box';
import { BreadcrumbSet } from '@tanium/react-breadcrumbs';
import styled, { css as cssEmotion } from '@tanium/react-emotion-9';
import { Icon } from '@tanium/react-graphics';
import { Grid, GridCell, GridGapConfig } from '@tanium/react-grid';
import { Stack } from '@tanium/react-stack';
import { css } from '@tanium/style-system';
import { ThemeColors, ThemeFontWeights } from '@tanium/theme-data';
import { DesriptionText, HeaderText } from './themeDefinitions';

export const PAGE_HEADER_COREUITYPE = 'PageHeader';

export interface PageHeaderBaseProps extends NeverChildren {
  /**
   * One or more navigation breadcrumbs. These should be Breadcrumb components, passed as a single
   * Breadcrumb, an array of Breadcrumbs, or a Fragment containing Breadcrumbs.
   */
  breadcrumbs?: JSX.Element | JSX.Element[];
  /**
   * One or more buttons to display in the header. These should be button components, passed as a single
   * button, an array of buttons, or a Fragment containing buttons.
   */
  buttons?: JSX.Element | JSX.Element[];
  /**
   * Optional additional content to be placed on the right side of the header, left of any supplied buttons.
   */
  additionalHeaderContent?: JSX.Element;
  /**
   * A description for the page to display under the main heading.
   */
  description?: React.ReactNode;
  /**
   * The main page heading.
   */
  heading: React.ReactNode;
  /**
   * Optional icon to display next to the heading.
   */
  icon?: Icon;
}

const Heading = styled('h1')(
  css({
    fontSize: '24px',
    fontWeight: ThemeFontWeights.Bold,
    lineHeight: '24px',
    marginTop: 0, // override bootstrap h1 styling
    marginBottom: 0, // override bootstrap h1 styling
    color: HeaderText,
  }),
);

const DescriptionCell = styled(GridCell)(
  css({
    fontSize: 14,
    color: DesriptionText,
    lineHeight: '16px',
  }),
);

const BreadcrumbsCell = styled(GridCell)(
  css({
    fontSize: 14,
    color: ThemeColors.Content2,
    lineHeight: '20px',
  }),
);

const buttonsStyle = cssEmotion({
  whiteSpace: 'nowrap',

  '& *:not(:last-child)': {
    marginRight: 8,
  },
});

const StyledHeader = styled('header', {
  shouldForwardProp: (prop) => prop !== 'hasButtons' && prop !== 'hasDescription',
})<{ hasButtons: boolean; hasDescription: boolean }>(({ hasButtons, hasDescription }) => {
  if (!hasDescription) {
    return css({
      // When there are no buttons, the distance between title and content is 24px.
      // When there are buttons, distance is measured from the bottom of the buttons instead
      // so we use a smaller margin to effectively still achieve 24px distance.
      mb: hasButtons ? 2 : 3,
    });
  }

  return css({
    mb: 2,
  });
});

const gridRowTemplate = ['auto'];
const gridGap: GridGapConfig = ['12px', '12px'];

const PageHeaderBaseInner = (props: PageHeaderBaseProps) => {
  const { breadcrumbs, buttons, additionalHeaderContent, icon: IconComponent } = props;
  const leftColumnAreas: (keyof PageHeaderBaseProps)[] = ['breadcrumbs', 'heading', 'description'];

  const hasButtonsOrAdditionalHeaderContent = !!(buttons || additionalHeaderContent);
  const columnTemplate = useMemo(
    () => ['1fr', ...(hasButtonsOrAdditionalHeaderContent ? ['auto'] : [])],
    [hasButtonsOrAdditionalHeaderContent],
  );

  const leftColumnAreasFiltered = leftColumnAreas.filter((area) => !!props[area]);

  if (leftColumnAreasFiltered.length === 0) {
    return null;
  }

  const templateAreas = leftColumnAreasFiltered
    .filter((area) => !!props[area])
    .map(
      (area) =>
        [area, buttons && area === 'description' ? 'description' : buttons && 'buttons'].filter(
          Boolean,
        ) as string[],
    );

  return (
    <Grid
      component={StyledHeader}
      hasButtons={!!buttons}
      hasDescription={!!props.description}
      templateAreas={templateAreas}
      columnTemplate={columnTemplate}
      rowTemplate={gridRowTemplate}
      gap={gridGap}
    >
      {breadcrumbs && (
        <BreadcrumbsCell area="breadcrumbs">
          {breadcrumbs && <BreadcrumbSet>{breadcrumbs}</BreadcrumbSet>}
        </BreadcrumbsCell>
      )}
      <GridCell component={Heading} area="heading">
        <Box display="flex" alignItems="center">
          {IconComponent && <Box as={IconComponent} display="block" size={3} mr={1} />}
          {props.heading}
        </Box>
      </GridCell>
      {!!props.description && (
        <DescriptionCell area="description">{props.description}</DescriptionCell>
      )}
      {(buttons || additionalHeaderContent) && (
        <GridCell area="buttons">
          <Stack itemSpacing={1}>
            {additionalHeaderContent}
            {buttons && <div className={buttonsStyle}>{buttons}</div>}
          </Stack>
        </GridCell>
      )}
    </Grid>
  );
};

export const PageHeaderBase = withCoreUIType(PAGE_HEADER_COREUITYPE)(PageHeaderBaseInner);
